// Port of `MergeTags` (SuiteModel/Journal.swift): placeholders a mailing platform fills in for each reader, such as
// `[FIRSTNAME]`, found in stored Markdown (an escaped marker's backslash optional) or plain text and kept as typed.
// Ranges are UTF-16, as NSRegularExpression's are.

export type MergeTagStyle = 'square' | 'double-brace' | 'mailchimp' | 'percent';
export const MERGE_TAG_STYLES: readonly MergeTagStyle[] = ['square', 'double-brace', 'mailchimp', 'percent'];

const PATTERNS: Record<MergeTagStyle, string> = {
  // Not a footnote reference (`[^1]`) or a span (`[ABC]{.sc}`).
  square: String.raw`\\?\[([A-Z][A-Z0-9_]*)\\?\](?!\{)`,
  'double-brace': String.raw`\{\{\s*([A-Za-z_][A-Za-z0-9_.]*)\s*\}\}`,
  mailchimp: String.raw`\\?\*\|([A-Z][A-Z0-9_]*)\|\\?\*`,
  percent: String.raw`%([A-Z][A-Z0-9_]*)%`,
};

export interface MergeTagMatch { start: number; end: number; name: string }

export const MergeTags = {
  styles: MERGE_TAG_STYLES,
  example(style: MergeTagStyle): string {
    return { square: '[FIRSTNAME]', 'double-brace': '{{first_name}}', mailchimp: '*|FNAME|*', percent: '%FIRSTNAME%' }[style];
  },
  from(raw: string): MergeTagStyle | null {
    return (MERGE_TAG_STYLES as readonly string[]).includes(raw) ? (raw as MergeTagStyle) : null;
  },

  /** Every tag in `text`, in order of position. */
  matches(text: string, styles: readonly MergeTagStyle[]): MergeTagMatch[] {
    if (!styles.length || !text) return [];
    const found: MergeTagMatch[] = [];
    for (const style of styles) {
      for (const m of text.matchAll(new RegExp(PATTERNS[style], 'g'))) {
        found.push({ start: m.index!, end: m.index! + m[0].length, name: m[1] });
      }
    }
    // Swift's `sorted` isn't stable, but two styles never match at the same place.
    return found.sort((a, b) => a.start - b.start);
  },

  /** The tags' names split into words ("FIRST_NAME" → FIRST, NAME). */
  words(text: string, styles: readonly MergeTagStyle[]): Set<string> {
    const out = new Set<string>();
    for (const m of MergeTags.matches(text, styles)) for (const w of m.name.split(/[^\p{L}\p{N}]+/u)) if (w) out.add(w);
    return out;
  },

  /** Stored Markdown with each tag written as typed: `\[FIRSTNAME\]` → `[FIRSTNAME]`. */
  verbatim(markdown: string, styles: readonly MergeTagStyle[]): string {
    if (!markdown.includes('\\')) return markdown;
    let out = markdown;
    for (const m of MergeTags.matches(markdown, styles).reverse()) {
      out = out.slice(0, m.start) + markdown.slice(m.start, m.end).replaceAll('\\', '') + out.slice(m.end);
    }
    return out;
  },

  /** Plain text with each tag replaced by `replacement(name)`. */
  masking(text: string, styles: readonly MergeTagStyle[], replacement: (name: string) => string = () => ''): string {
    const found = MergeTags.matches(text, styles);
    let out = text;
    for (const m of found.reverse()) out = out.slice(0, m.start) + replacement(m.name) + out.slice(m.end);
    return out;
  },

  /** How Read Aloud says a tag. */
  spoken(name: string): string {
    const known: Record<string, string> = {
      FIRSTNAME: 'first name', FNAME: 'first name', LASTNAME: 'last name', LNAME: 'last name', FULLNAME: 'full name', EMAIL: 'email',
    };
    return known[name.toUpperCase()] ?? name.replaceAll('_', ' ').replaceAll('.', ' ').toLowerCase();
  },

  /** Text that looks like a tag gone wrong: `[FIRSTNAME` (unclosed), `[FirstName]` (not in capitals). */
  broken(plain: string, styles: readonly MergeTagStyle[]): string[] {
    if (!styles.includes('square')) return [];
    const found: string[] = [];
    for (const m of plain.matchAll(/\[([A-Za-z][A-Za-z0-9_]{2,})(\]?)/g)) {
      const name = m[1];
      const closed = m[2] !== '';
      const upper = name === name.toUpperCase();
      if (closed && upper) continue;
      const lower = name.toLowerCase();
      if (closed && !(name.includes('_') || lower.startsWith('first') || lower.startsWith('last') || lower.endsWith('name'))) continue;
      if (!closed && !upper) continue;
      found.push(m[0]);
    }
    return found;
  },

  /** `mergeTagStyles()`: the stored choice (`settings.mergeTags`, a JSON array), else `[square]` for a journal. */
  stylesOf(settingsValue: unknown, isJournal: boolean): MergeTagStyle[] {
    let raw: unknown = settingsValue;
    if (typeof raw === 'string') {
      try { raw = JSON.parse(raw); } catch { raw = undefined; }
    }
    if (Array.isArray(raw)) return raw.flatMap((r) => (typeof r === 'string' && MergeTags.from(r) ? [r as MergeTagStyle] : []));
    return isJournal ? ['square'] : [];
  },
};
